// PuzzlePage.jsx — tek bir tsume bulmacasını interaktif çözme sayfası (/bulmacalar/:id).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import Seo, { breadcrumbLd } from '../components/Seo';
import PhaserBoard from '../components/PhaserBoard';
import HandRow from '../components/HandRow';
import useBoardInteraction from '../components/useBoardInteraction';
import { Shogi, PIECE_NAMES, PROMOTED_NAMES } from '../lib/ShogiEngine';
import { PUZZLES, getPuzzle, checkPuzzleMove, puzzleReply } from '../lib/puzzles';
import { markSolved, getSolved } from '../lib/puzzleProgress';
import { logAnalyticsEvent } from '../firebase';
import { useLang } from '../i18n';
import { PUZZLE_TEXT } from './PuzzlesPage';

const T = {
  tr: {
    task: 'Sente oynar, {n} hamlede mat eder.',
    yourMove: 'Sıra sende: şah çekerek başla.',
    good: 'Doğru! Rakip cevap veriyor…',
    continue: 'Doğru. Devam et: yine şah çek.',
    wrong: 'Bu hamle mata götürmüyor. Tekrar dene.',
    solved: 'Tebrikler, mat ettin! 🎉',
    shown: 'Çözüm gösterildi.',
    hint: 'İpucu',
    solution: 'Çözümü Göster',
    reset: 'Baştan',
    next: 'Sonraki Bulmaca',
    prev: 'Önceki',
    all: 'Tüm bulmacalar',
    hand: 'Elin',
    goteHand: 'Rakibin eli',
    solutionTitle: 'Çözüm (USI notasyonu)',
    solutionNote: 'Sente ve Gote hamleleri sırayla.',
    already: 'Bu bulmacayı daha önce çözdün.',
  },
  en: {
    task: 'Sente to play and mate in {n}.',
    yourMove: 'Your move: start with a check.',
    good: 'Correct! The defender replies…',
    continue: 'Correct. Keep going: give check again.',
    wrong: 'That move does not lead to mate. Try again.',
    solved: 'Checkmate — well done! 🎉',
    shown: 'Solution shown.',
    hint: 'Hint',
    solution: 'Show Solution',
    reset: 'Restart',
    next: 'Next Puzzle',
    prev: 'Previous',
    all: 'All puzzles',
    hand: 'Your hand',
    goteHand: "Defender's hand",
    solutionTitle: 'Solution (USI notation)',
    solutionNote: 'Sente and Gote moves in order.',
    already: 'You have solved this puzzle before.',
  },
};

export default function PuzzlePage() {
  const { id } = useParams();
  const { to } = useLang();
  const puzzle = getPuzzle(id);
  if (!puzzle) return <Navigate to={to('puzzles')} replace />;
  // key: bulmaca değişince tüm durum sıfırlansın
  return <PuzzleSolver key={puzzle.id} puzzle={puzzle} />;
}

function PuzzleSolver({ puzzle }) {
  const { lang, to, t } = useLang();
  const tx = T[lang];
  const list = PUZZLE_TEXT[lang];
  const index = PUZZLES.findIndex((p) => p.id === puzzle.id);
  const prev = PUZZLES[index - 1];
  const next = PUZZLES[index + 1];

  const engineRef = useRef(null);
  if (!engineRef.current) engineRef.current = Shogi.fromSfen(puzzle.sfen);
  const engine = engineRef.current;

  const [snap, setSnap] = useState(() => ({ board: engine.board(), hands: engine.hands(), lastMove: null }));
  const [ply, setPly] = useState(0);
  const [state, setState] = useState('play'); // play | waiting | solved | shown
  const [feedback, setFeedback] = useState({ kind: 'info', text: tx.yourMove });
  const [showHint, setShowHint] = useState(false);
  const timers = useRef([]);
  const alreadySolved = useMemo(() => getSolved().has(puzzle.id), [puzzle.id]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));

  const sync = useCallback((lastMove = null) => {
    setSnap({ board: engine.board(), hands: engine.hands(), lastMove });
  }, [engine]);

  const onMove = useCallback((descriptor) => {
    const verdict = checkPuzzleMove(puzzle, engine, ply, descriptor);
    if (verdict === 'wrong') {
      setFeedback({ kind: 'bad', text: tx.wrong });
      logAnalyticsEvent('puzzle_wrong', { puzzle_id: puzzle.id, ply });
      return;
    }
    const record = engine.applyDescriptor(descriptor);
    sync(record);
    if (verdict === 'solved') {
      setState('solved');
      setFeedback({ kind: 'ok', text: tx.solved });
      markSolved(puzzle.id);
      logAnalyticsEvent('puzzle_solved', { puzzle_id: puzzle.id, level: puzzle.level });
      return;
    }
    // Doğru ara hamle: rakip cevabı
    setState('waiting');
    setFeedback({ kind: 'ok', text: tx.good });
    later(() => {
      // Ana varyanttaysa çözümdeki cevap, değilse çözücünün en uzun direnişi
      const replyDesc = puzzleReply(puzzle, engine, ply + 1);
      if (replyDesc) sync(engine.applyDescriptor(replyDesc));
      setPly((p) => p + 2);
      setState('play');
      setFeedback({ kind: 'info', text: tx.continue });
    }, 650);
  }, [puzzle, engine, ply, sync, tx]);

  const interaction = useBoardInteraction(engine, { color: 'sente', canAct: state === 'play', onMove });

  const reset = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    engine.loadSfen(puzzle.sfen);
    interaction.clear();
    setPly(0);
    setState('play');
    setShowHint(false);
    setFeedback({ kind: 'info', text: tx.yourMove });
    sync(null);
  };

  const showSolution = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    engine.loadSfen(puzzle.sfen);
    interaction.clear();
    sync(null);
    setState('shown');
    setFeedback({ kind: 'info', text: tx.shown });
    logAnalyticsEvent('puzzle_solution_shown', { puzzle_id: puzzle.id });
    puzzle.solution.forEach((usi, i) => {
      later(() => sync(engine.applyDescriptor(engine.parseUsi(usi))), 700 * (i + 1));
    });
  };

  const title = `${puzzle.title[lang]} — ${list.mateIn.replace('{n}', puzzle.mateIn)}`;
  const seoDesc = lang === 'en'
    ? `Shogi tsume puzzle #${index + 1} (${t(`level.${puzzle.level}`)}): Sente to play and mate in ${puzzle.mateIn}. Solve it on an interactive board, with hint and solution.`
    : `Şogi tsume bulmacası #${index + 1} (${t(`level.${puzzle.level}`)}): Sente oynar, ${puzzle.mateIn} hamlede mat eder. İnteraktif tahtada çöz; ipucu ve çözüm mevcut.`;

  const ld = [
    {
      '@context': 'https://schema.org',
      '@type': 'Quiz',
      name: title,
      about: lang === 'en' ? 'Tsume shogi (shogi mate problem)' : 'Tsume şogi (şogi mat problemi)',
      educationalLevel: puzzle.level,
      inLanguage: lang,
      isPartOf: { '@type': 'CreativeWork', name: list.title },
    },
    breadcrumbLd([
      { name: t('brand'), path: to('home') },
      { name: list.short, path: to('puzzles') },
      { name: puzzle.title[lang], path: to('puzzle', { id: puzzle.id }) },
    ]),
  ];

  return (
    <>
      <Seo title={title} description={seoDesc} jsonLd={ld} />
      <div className="container py-3 py-md-4">
        <nav className="crumbs" aria-label="breadcrumb">
          <Link to={to('home')}>{t('brand')}</Link> / <Link to={to('puzzles')}>{list.short}</Link> / <span aria-current="page">#{index + 1}</span>
        </nav>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
          <h1 className="mb-0" style={{ fontSize: 'clamp(1.6rem, 4vw, 2.4rem)' }}>{puzzle.title[lang]}</h1>
          <span className={`tag ${puzzle.level}`}>{t(`level.${puzzle.level}`)}</span>
          <span className="tag">{list.mateIn.replace('{n}', puzzle.mateIn)}</span>
        </div>
        <p className="text-muted mb-3">{tx.task.replace('{n}', puzzle.mateIn)} {alreadySolved && <span className="tag easy ms-1">✓ {tx.already}</span>}</p>

        <div className="row g-3 g-lg-4">
          <div className="col-12 col-lg-8">
            <div className="card">
              <div className="card-body d-flex flex-column gap-2 p-2 p-sm-3">
                <HandRow label={tx.goteHand} hand={snap.hands.gote} />
                <div className="board-wrap">
                  <PhaserBoard
                    rows={9}
                    cols={9}
                    cellSize={64}
                    board={snap.board}
                    lastMove={snap.lastMove}
                    pieceLabels={PIECE_NAMES}
                    promotedLabels={PROMOTED_NAMES}
                    selected={interaction.selected}
                    legalTargets={interaction.legalTargets}
                    onSquareClick={interaction.handleSquareClick}
                  />
                </div>
                <HandRow
                  label={tx.hand}
                  hand={snap.hands.sente}
                  active={state === 'play'}
                  dropType={interaction.dropType}
                  onPick={interaction.handleHandClick}
                />
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-4">
            <div className="panel d-flex flex-column gap-3">
              <div className={`feedback ${feedback.kind}`} role="status" aria-live="polite">{feedback.text}</div>

              <div className="d-flex flex-wrap gap-2">
                <button type="button" className="btn btn-soft btn-sm" onClick={() => setShowHint((s) => !s)}>💡 {tx.hint}</button>
                <button type="button" className="btn btn-soft btn-sm" onClick={reset}>↺ {tx.reset}</button>
                <button type="button" className="btn btn-soft btn-sm" onClick={showSolution}>{tx.solution}</button>
              </div>
              {showHint && <div className="feedback info fw-normal">💡 {puzzle.hint[lang]}</div>}

              {next ? (
                <Link to={to('puzzle', { id: next.id })} className="btn btn-ink">{tx.next} →</Link>
              ) : (
                <Link to={to('puzzles')} className="btn btn-ink">{tx.all}</Link>
              )}
              <div className="d-flex justify-content-between small">
                {prev ? <Link to={to('puzzle', { id: prev.id })}>← {tx.prev}</Link> : <span />}
                <Link to={to('puzzles')}>{tx.all}</Link>
              </div>

              <details className="small">
                <summary className="fw-bold">{tx.solutionTitle}</summary>
                <p className="text-muted mb-1 mt-2">{tx.solutionNote}</p>
                <ol className="mb-0">{puzzle.solution.map((m, i) => <li key={i}><code>{m}</code> {i % 2 === 0 ? '▲' : '△'}</li>)}</ol>
              </details>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
